import React, { useCallback, useEffect, useState } from "react";
import { listAllTutors, updateTutor, deleteTutor, createTutor, adminStats } from "../lib/api";
import { AdminStats } from "../components/admin/AdminStats";
import { AdminTutorRows } from "../components/admin/AdminTutorRows";
import { AdminTutorForm } from "../components/admin/AdminTutorForm";
import { toast } from "sonner";

const emptyForm = {
  display_name: "",
  country: "Reino Unido",
  country_code: "UK",
  accent: "British RP",
  specialty: "Conversación fluida",
  bio: "",
  price_per_second_cents: 4,
  initials: "",
  avatar_color: "#7228C8",
  status: "pendiente",
  approved: false,
};

export default function AdminPage() {
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // pending, all
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([listAllTutors(), adminStats()]);
      setTutors(t);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const approve = async (t) => {
    try {
      await updateTutor(t.id, { approved: true, status: "aprobado" });
      toast.success(`${t.display_name} aprobado`);
      fetchAll();
    } catch (e) { toast.error("No se pudo aprobar"); }
  };
  const reject = async (t) => {
    if (!window.confirm(`¿Rechazar a ${t.display_name}? Se elimina.`)) return;
    try {
      await deleteTutor(t.id);
      toast.success(`${t.display_name} eliminado`);
      fetchAll();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };
  const markIncident = async (t) => {
    try {
      await updateTutor(t.id, { status: "incidencia" });
      toast.success("Marcado con incidencia");
      fetchAll();
    } catch (e) { toast.error("No pudo"); }
  };

  const validate = () => {
    const e = {};
    if (!form.display_name.trim() || form.display_name.length < 3) e.display_name = "Al menos 3 letras.";
    if (!/^[A-Z][a-z]+ [A-Z]\.?$/.test(form.display_name.trim())) e.display_name = "Formato: Nombre Inicial. (ej. 'Sarah M.')";
    if (!form.initials.trim() || form.initials.length !== 2) e.initials = "Dos letras exactas.";
    if (!form.bio.trim() || form.bio.length < 20) e.bio = "Al menos 20 caracteres.";
    if (form.price_per_second_cents < 1 || form.price_per_second_cents > 20) e.price_per_second_cents = "Entre 1 y 20 céntimos.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      await createTutor(form);
      toast.success("Tutor añadido · pendiente de aprobación");
      setShowAdd(false);
      setForm(emptyForm);
      setErrors({});
      fetchAll();
    } catch (e) {
      toast.error("No se pudo añadir");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = tab === "pending" ? tutors.filter((t) => !t.approved) : tutors;
  const updateForm = (changes) => setForm((current) => ({ ...current, ...changes }));
  const closeForm = () => {
    setShowAdd(false);
    setForm(emptyForm);
    setErrors({});
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="wk-eyebrow mb-4">Panel interno</div>
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <h1 className="text-[42px] lg:text-[56px] font-extrabold tracking-hero leading-[1.05]" style={{ color: "#1E1924" }}>
            Gestionar tutores.<br />A mano.
          </h1>
          <p className="mt-4 text-[15px] max-w-prose-tight leading-[1.6]" style={{ color: "#70667A" }}>
            Añade perfiles, revísalos y apruébalos antes de mostrarlos en el catálogo público.
          </p>
        </div>
        <button onClick={() => setShowAdd((s) => !s)} className="wk-btn wk-btn-primary" data-testid="admin-add-toggle-btn">
          {showAdd ? "Cerrar formulario" : "Añadir tutor"}
        </button>
      </div>

      <AdminStats stats={stats} />

      {showAdd ? <AdminTutorForm errors={errors} form={form} onCancel={closeForm} onChange={updateForm} onSubmit={submit} submitting={submitting} /> : null}

      {/* Tabs */}
      <div className="mt-14 flex items-center gap-2 border-b" style={{ borderColor: "#DEDAE2" }}>
        <button onClick={() => setTab("pending")} className="px-4 py-3 text-[14px] font-medium relative" style={{ color: tab === "pending" ? "#1E1924" : "#70667A" }} data-testid="admin-tab-pending">
          Pendientes
          {tab === "pending" && <div className="absolute -bottom-px left-0 right-0 h-0.5" style={{ background: "#7228C8" }} />}
        </button>
        <button onClick={() => setTab("all")} className="px-4 py-3 text-[14px] font-medium relative" style={{ color: tab === "all" ? "#1E1924" : "#70667A" }} data-testid="admin-tab-all">
          Todos ({tutors.length})
          {tab === "all" && <div className="absolute -bottom-px left-0 right-0 h-0.5" style={{ background: "#7228C8" }} />}
        </button>
      </div>

      <AdminTutorRows
        loading={loading}
        tutors={filtered}
        tab={tab}
        onApprove={approve}
        onReject={reject}
        onIncident={markIncident}
      />
    </div>
  );
}
